import { useAuthActions } from '@convex-dev/auth/react';
import type { VisualTheme } from '../App';

interface Props {
  onNavigate: (view: 'home' | 'history') => void;
  currentView: 'home' | 'history';
  visualTheme: VisualTheme;
  onToggleTheme: () => void;
}

export function Header({ onNavigate, currentView, visualTheme, onToggleTheme }: Props) {
  const { signOut } = useAuthActions();

  return (
    <header className="reveal-up">
      <div className="flex items-baseline justify-between gap-6 flex-wrap">
        <div>
          <p className="label-mono mb-3">
            <span className="text-[var(--color-amber-mid)]">
              {visualTheme === 'matrix' ? 'root@matrix' : 'neo@brutal'}
            </span>
            <span className="text-[var(--color-asphalt-500)]">:</span>
            <span className="text-[var(--color-asphalt-700)]">~/audio</span>
            <span className="text-[var(--color-asphalt-500)]">$</span>{' '}
            ./transcribe.sh --init
          </p>
          <h1
            className="heading-display glitch text-6xl sm:text-7xl md:text-8xl"
            data-text="SYS_TRANSCRIBE"
          >
            SYS_TRANSCRIBE
          </h1>
          <p className="font-mono text-xs text-[var(--color-asphalt-500)] mt-3 tracking-[0.18em]">
            // <span className="text-[var(--color-amber-mid)]">audio</span> ─→{' '}
            <span className="text-[var(--color-amber-mid)]">stt_model</span> ─→{' '}
            <span className="text-[var(--color-amber-mid)]">summary</span>
            <span className="term-caret" aria-hidden />
          </p>
        </div>

        <nav className="flex flex-col gap-2 text-right">
          <button
            type="button"
            onClick={onToggleTheme}
            className="theme-toggle no-print"
            aria-label={visualTheme === 'matrix' ? 'Activar tema Neobrutalism' : 'Activar tema Matrix'}
            title={visualTheme === 'matrix' ? 'Activar tema Neobrutalism' : 'Activar tema Matrix'}
          >
            <ThemeIcon />
            <span>{visualTheme === 'matrix' ? 'neo.red' : 'matrix'}</span>
          </button>
          <NavLink
            label="new_meeting"
            active={currentView === 'home'}
            onClick={() => onNavigate('home')}
          />
          <NavLink
            label="ls --history"
            active={currentView === 'history'}
            onClick={() => onNavigate('history')}
          />
          <button
            type="button"
            onClick={() => void signOut()}
            className="term-link term-link-danger text-right no-print"
          >
            logout --exit
          </button>
        </nav>
      </div>

      <div className="tech-rule mt-10 mb-2" />
      <p className="label-mono">
        <span className="text-[var(--color-amber-mid)]">[01]</span> · boot --ok
        <span className="text-[var(--color-asphalt-300)]"> // upload → transcribe → summarize → persist</span>
      </p>
    </header>
  );
}

function ThemeIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M4 14.5C4 9.8 7.8 6 12.5 6H15a5 5 0 0 1 0 10h-.5a1.5 1.5 0 0 0-1.5 1.5A2.5 2.5 0 0 1 10.5 20H10a6 6 0 0 1-6-5.5Z"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinejoin="round"
      />
      <path d="M8 12h.01M11 9.5h.01M15 10h.01" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

function NavLink({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'font-mono text-xs uppercase tracking-[0.14em] py-1 transition-all',
        active
          ? 'text-[var(--color-amber-mid)] glow-cyan'
          : 'text-[var(--color-asphalt-500)] hover:text-[var(--color-asphalt)]',
      ].join(' ')}
    >
      <span className="inline-block">
        <span className={active ? 'text-[var(--color-amber-mid)]' : 'text-[var(--color-asphalt-300)]'}>
          {active ? '▶ ' : '  '}
        </span>
        {label}
      </span>
    </button>
  );
}
